/**
 * AuthGuard — protects routes that require authentication.
 * Uses demo AuthContext (replaces Clerk for POC).
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth, useUser } from '../../contexts/AuthContext';
import LoadingScreen from './LoadingScreen';
import { LEGACY_PORTAL_ENABLED } from '../../lib/portal-scope';

export default function AuthGuard({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: string;
}) {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/sign-in');
    }
  }, [isLoaded, isSignedIn, navigate]);

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (!isSignedIn) {
    return null;
  }

  if (requiredRole) {
    const userRole = (user?.publicMetadata as { role?: string } | undefined)?.role || 'patient';
    if (userRole !== requiredRole && userRole !== 'admin' && userRole !== 'super_admin') {
      const isNonMember = userRole === 'non_member';
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-text mb-2">
              {isNonMember ? 'Members Only' : 'Access Denied'}
            </h1>
            <p className="text-text-secondary mb-6">
              {isNonMember
                ? 'This feature requires a member account. Sign up to access appointments, medical records, prescriptions, telehealth, and more.'
                : 'You do not have permission to access this page.'}
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate(LEGACY_PORTAL_ENABLED ? '/dashboard' : '/')} className="px-4 py-2 bg-blue-600 text-white font-semibold text-sm rounded-lg hover:bg-blue-700 transition-colors">
                {LEGACY_PORTAL_ENABLED ? 'Return to Dashboard' : 'Go back'}
              </button>
              {LEGACY_PORTAL_ENABLED && isNonMember && (
                <button onClick={() => navigate('/sign-up')} className="px-4 py-2 border border-blue-600 text-blue-600 font-semibold text-sm rounded-lg hover:bg-blue-50 transition-colors">
                  Become a Member
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
}
